import React, { createContext, useContext, useMemo, useState } from "react";
import { Alert } from "react-bootstrap";
import ProductsSearch from "@admin/components/ProductsSearch";

import { uploadFile } from "@admin/domain/files/api";
import { FileModel } from "@admin/domain/files/model/FileModel";
import { archiveQuiz, deleteQuiz, publishQuiz, QuizApiError, saveDraft } from "@admin/domain/quizzes/api";
import type {
    QuizDefinition,
    QuizImageRef,
    QuizModel,
    QuizOption,
    QuizQuestion,
    QuizResult,
    QuizTransition,
} from "@admin/domain/quizzes/models/QuizModel";

const uid = (): string => {
    if (typeof crypto !== `undefined` && typeof crypto.randomUUID === `function`) {
        return crypto.randomUUID();
    }

    return `xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx`.replace(/[xy]/g, (char) => {
        const random = (Math.random() * 16) | 0;
        const value = char === `x` ? random : (random & 0x3) | 0x8;

        return value.toString(16);
    });
};

const emptyTransition = (): QuizTransition => ({ type: `inherit`, question_id: null });

const defaultContactFields = { full_name: true, email: true, phone: true, privacy_policy_agreement: true };
const contactFieldLabels = { full_name: `ФИО`, phone: `Телефон`, email: `Email`, privacy_policy_agreement: `Согласие на обработку персональных данных` };

// Laravel сохраняет пустые строки черновика как null.
const normalizeDefinition = (definition: QuizDefinition): QuizDefinition => ({
    ...definition,
    contact_fields: { ...defaultContactFields, ...definition.contact_fields },
    questions: definition.questions.map((question) => ({
        ...question, text: question.text ?? ``,
        images: question.images.map((image) => ({ ...image, alt: image.alt ?? `` })),
        options: question.options.map((option) => ({ ...option, text: option.text ?? `` })),
    })),
    results: definition.results.map((result) => ({
        ...result, internal_name: result.internal_name ?? ``, title: result.title ?? ``, text: result.text ?? ``,
        image: result.image ? { ...result.image, alt: result.image.alt ?? `` } : null,
    })),
    thank_you: {
        ...definition.thank_you,
        image: definition.thank_you.image ? { ...definition.thank_you.image, alt: definition.thank_you.image.alt ?? `` } : null,
        button: definition.thank_you.button ? { label: definition.thank_you.button.label ?? ``, url: definition.thank_you.button.url ?? `` } : null,
    },
});

const emptyDefinition = (): QuizDefinition => ({
    schema_version: 1,
    start_question_id: null,
    time_limit_seconds: null,
    allow_back: false,
    contact_position: `none`,
    thank_you: { image: null, text: null, button: null },
    questions: [],
    results: [],
});

const emptyOption = (): QuizOption => ({ id: uid(), text: ``, weight: 0, transition: emptyTransition() });

const emptyQuestion = (): QuizQuestion => ({
    id: uid(),
    type: `single_choice`,
    text: ``,
    images: [],
    shuffle_answers: false,
    default_transition: { type: `finish`, question_id: null },
    options: [emptyOption(), emptyOption()],
});

const emptyResult = (): QuizResult => ({
    id: uid(),
    internal_name: ``,
    title: ``,
    text: ``,
    image: null,
    min_score: null,
    max_score: null,
    is_default: false,
});

type QuestionLabel = (id: string) => string;
const UploadStateContext = createContext<(delta: number) => void>(() => undefined);

const transitionOptions = (withInherit: boolean) => [
    ...(withInherit ? [{ value: `inherit`, label: `По настройке вопроса` }] : []),
    { value: `question`, label: `К вопросу…` },
    { value: `finish`, label: `Завершить вопросы` },
];

type TransitionEditorProps = {
    value: QuizTransition;
    withInherit: boolean;
    questionLabel: QuestionLabel;
    questions: QuizQuestion[];
    onChange: (value: QuizTransition) => void;
};

const TransitionEditor = ({ value, withInherit, questionLabel, questions, onChange }: TransitionEditorProps) => {
    const questionId = value.question_id || ``;

    return (
        <div className="row">
            <div className="col-6">
                <select
                    className="form-select"
                    value={value.type}
                    onChange={(event) =>
                        onChange({
                            type: event.target.value as QuizTransition[`type`],
                            question_id: event.target.value === `question` ? questionId : null,
                        })
                    }
                >
                    {transitionOptions(withInherit).map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>
            {value.type === `question` && (
                <div className="col-6">
                    <select className="form-select" aria-label="Следующий вопрос" value={questionId}
                        onChange={(event) => onChange({ ...value, question_id: event.target.value || null })}>
                        <option value="">Выберите вопрос</option>
                        {questions.map((question) => <option key={question.id} value={question.id}>{questionLabel(question.id)}</option>)}
                    </select>
                </div>
            )}
        </div>
    );
};

type ImagesEditorProps = {
    images: QuizImageRef[];
    limit?: number;
    onChange: (images: QuizImageRef[]) => void;
};

const ImagesEditor = ({ images, onChange, limit = 10 }: ImagesEditorProps) => {
    const changeUploadCount = useContext(UploadStateContext);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const addImages = async (files: File[]) => {
        setError(null);
        if (files.length + images.length > limit) { setError(`Максимум изображений: ${limit}`); return; }
        if (files.some((file) => ![`image/jpeg`, `image/png`, `image/webp`].includes(file.type) || file.size > 10 * 1024 * 1024)) {
            setError(`Выберите JPEG, PNG или WebP размером до 10 МБ`); return;
        }
        setUploading(true);
        changeUploadCount(1);
        try {
            const uploaded = await Promise.all(files.map((file) => uploadFile(file)));
            onChange([...images, ...uploaded.map((file: FileModel) => ({ file_id: file.id, alt: `` }))]);
        } catch { setError(`Не удалось загрузить изображения. Повторите загрузку.`); }
        finally { setUploading(false); changeUploadCount(-1); }
    };
    return <fieldset disabled={uploading} className="mb-3">
        <legend className="form-label">Изображения</legend>
        {images.map((image, index) => <div key={index} className="d-flex align-items-center gap-2 mb-2">
            <span>#{image.file_id}</span>
            <input className="form-control" aria-label="Описание изображения" placeholder="Альтернативный текст" value={image.alt}
                onChange={(event) => onChange(images.map((item, i) => (i === index ? { ...item, alt: event.target.value } : item)))} />
            <button type="button" disabled={index === 0} onClick={() => {
                const next = [...images]; [next[index - 1], next[index]] = [next[index], next[index - 1]]; onChange(next);
            }}>↑</button>
            <button type="button" onClick={() => onChange(images.filter((_, i) => i !== index))}>Удалить</button>
        </div>)}
        {images.length < limit && <input type="file" accept="image/jpeg,image/png,image/webp" multiple={limit > 1} className="form-control"
            onChange={(event) => { const files = Array.from(event.target.files || []); event.target.value = ``; void addImages(files); }} />}
        {uploading && <p>Загружаем изображения… Дождитесь окончания перед сохранением.</p>}
        {error && <p role="alert" className="text-danger">{error}</p>}
    </fieldset>;
};

type QuestionEditorProps = {
    question: QuizQuestion;
    questionLabel: QuestionLabel;
    questions: QuizQuestion[];
    onUpdate: (question: QuizQuestion) => void;
    onRemove: () => void;
    onMove: (direction: -1 | 1) => void;
    onDuplicate: () => void;
};

const QuestionEditor = ({ question, questionLabel, questions, onUpdate, onRemove, onMove, onDuplicate }: QuestionEditorProps) => {
    return (
        <div className="border rounded p-3 mb-3">
            <div className="d-flex justify-content-between mb-2">
                <h5>Вопрос {question.id.slice(0, 8)}</h5>
                <div>
                    <button type="button" className="btn btn-sm btn-outline-secondary me-1" onClick={() => onMove(-1)}>
                        ↑
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-secondary me-1" onClick={() => onMove(1)}>
                        ↓
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={onRemove}>
                        Удалить
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-secondary ms-1" onClick={onDuplicate}>
                        Дублировать
                    </button>
                </div>
            </div>

            <textarea
                className="form-control mb-2"
                placeholder="Текст вопроса"
                value={question.text}
                onChange={(event) => onUpdate({ ...question, text: event.target.value })}
            />

            <ImagesEditor
                images={question.images}
                onChange={(images) => onUpdate({ ...question, images })}
            />

            <div className="form-check mb-2">
                <input
                    type="checkbox"
                    className="form-check-input"
                    checked={question.shuffle_answers}
                    onChange={(event) => onUpdate({ ...question, shuffle_answers: event.target.checked })}
                />
                <label className="form-check-label">Перемешивать варианты</label>
            </div>

            <div className="mb-2">
                <label className="form-label">Переход вопроса по умолчанию</label>
                <TransitionEditor
                    withInherit={false}
                    value={question.default_transition}
                    questionLabel={questionLabel}
                    questions={questions}
                    onChange={(transition) => onUpdate({ ...question, default_transition: transition })}
                />
            </div>

            <h6>Варианты ответа</h6>
            {question.options.map((option, index) => (
                <div key={option.id} className="border rounded p-2 mb-2">
                    <div className="d-flex gap-2 mb-2">
                        <input
                            className="form-control"
                            placeholder="Текст варианта"
                            value={option.text}
                            onChange={(event) =>
                                onUpdate({
                                    ...question,
                                    options: question.options.map((item, itemIndex) =>
                                        (itemIndex === index ? { ...item, text: event.target.value } : item),
                                    ),
                                })
                            }
                        />
                        <input
                            className="form-control"
                            style={{ maxWidth: 140 }}
                            type="number"
                            placeholder="Вес"
                            value={option.weight}
                            onChange={(event) =>
                                onUpdate({
                                    ...question,
                                    options: question.options.map((item, itemIndex) =>
                                        (itemIndex === index ? { ...item, weight: Number(event.target.value) || 0 } : item),
                                    ),
                                })
                            }
                        />
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() =>
                                onUpdate({
                                    ...question,
                                    options: question.options.filter((_, itemIndex) => itemIndex !== index),
                                })
                            }
                        >
                            ×
                        </button>
                    </div>
                    <div className="d-flex gap-2 mb-2">
                        <small>ID: {option.id}</small>
                        <button type="button" disabled={index === 0} onClick={() => {
                            const options = [...question.options]; [options[index - 1], options[index]] = [options[index], options[index - 1]]; onUpdate({ ...question, options });
                        }}>↑</button>
                        <button type="button" disabled={index === question.options.length - 1} onClick={() => {
                            const options = [...question.options]; [options[index + 1], options[index]] = [options[index], options[index + 1]]; onUpdate({ ...question, options });
                        }}>↓</button>
                    </div>
                    <TransitionEditor
                        withInherit
                        value={option.transition}
                        questionLabel={questionLabel}
                    questions={questions}
                        onChange={(transition) =>
                            onUpdate({
                                ...question,
                                options: question.options.map((item, itemIndex) =>
                                    (itemIndex === index ? { ...item, transition } : item),
                                ),
                            })
                        }
                    />
                </div>
            ))}

            <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() => onUpdate({ ...question, options: [...question.options, emptyOption()] })}
            >
                Добавить вариант
            </button>
        </div>
    );
};

type ResultEditorProps = {
    result: QuizResult;
    onUpdate: (result: QuizResult) => void;
    onRemove: () => void;
};

const ResultEditor = ({ result, onUpdate, onRemove }: ResultEditorProps) => {
    const productIds = result.product_ids || [];
    const [productTitles, setProductTitles] = useState<Record<string, string>>(window.CMS.quiz?.product_titles || {});
    return (
        <div className="border rounded p-3 mb-3">
            <div className="d-flex justify-content-between mb-2">
                <h5>Результат {result.id.slice(0, 8)}</h5>
                <button type="button" className="btn btn-sm btn-outline-danger" onClick={onRemove}>
                    Удалить
                </button>
            </div>

            <div className="form-check mb-2">
                <input
                    type="checkbox"
                    className="form-check-input"
                    checked={result.is_default}
                    onChange={(event) => onUpdate({ ...result, is_default: event.target.checked, min_score: null, max_score: null })}
                />
                <label className="form-check-label">По умолчанию (fallback)</label>
            </div>

            <label className="form-label">Тип результата</label>
            <select className="form-select mb-3" value={result.kind || `text`} onChange={(event) => onUpdate({ ...result, kind: event.target.value as QuizResult[`kind`], product_ids: [] })}>
                <option value="text">Текст и изображение</option>
                <option value="product">Один товар — автоматически в корзину</option>
                <option value="products">Группа товаров — карточки с покупкой</option>
            </select>
            {result.kind && result.kind !== `text` && <div className="mb-3">
                {productIds.map((id, index) => <div key={`${index}-${id}`}>
                    <ProductsSearch inputName={`quiz-result-${result.id}-${index}`} productId={id} productTitle={productTitles[id] || id}
                        onChange={(value, title) => { if (value) setProductTitles({ ...productTitles, [value]: title || value }); onUpdate({ ...result, product_ids: productIds.map((item, i) => (i === index ? value || `` : item)) }); }} />
                    <button type="button" onClick={() => onUpdate({ ...result, product_ids: productIds.filter((_, i) => i !== index) })}>Убрать товар</button>
                </div>)}
                <button type="button" disabled={productIds.length >= (result.kind === `product` ? 1 : 50)} onClick={() => onUpdate({ ...result, product_ids: [...productIds, ``] })}>Добавить товар в результат</button>
                <p>{result.kind === `product` ? `После завершения автоматически добавится 1 единица товара. Повторный показ результата не добавляет его ещё раз.` : `Товары выводятся в выбранном порядке; покупатель добавляет их кнопками карточек.`}</p>
            </div>}

            <input
                className="form-control mb-2"
                placeholder="Внутреннее название"
                value={result.internal_name}
                onChange={(event) => onUpdate({ ...result, internal_name: event.target.value })}
            />
            <input
                className="form-control mb-2"
                placeholder="Публичный заголовок"
                value={result.title}
                onChange={(event) => onUpdate({ ...result, title: event.target.value })}
            />
            <textarea
                className="form-control mb-2"
                placeholder="Текст результата"
                value={result.text}
                onChange={(event) => onUpdate({ ...result, text: event.target.value })}
            />

            <ImagesEditor images={result.image ? [result.image] : []} limit={1} onChange={(images) => onUpdate({ ...result, image: images[0] || null })} />

            {!result.is_default && (
                <div className="row mb-2">
                    <div className="col-6">
                        <label className="form-label">Минимум</label>
                        <input
                            className="form-control"
                            type="number"
                            value={result.min_score ?? ``}
                            onChange={(event) =>
                                onUpdate({ ...result, min_score: event.target.value === `` ? null : Number(event.target.value) })
                            }
                        />
                    </div>
                    <div className="col-6">
                        <label className="form-label">Максимум</label>
                        <input
                            className="form-control"
                            type="number"
                            value={result.max_score ?? ``}
                            onChange={(event) =>
                                onUpdate({ ...result, max_score: event.target.value === `` ? null : Number(event.target.value) })
                            }
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

const QuizEditor: React.FC = () => {
    const quiz: QuizModel = window.CMS.quiz;
    const [title, setTitle] = useState(quiz.title ?? ``);
    const [slug, setSlug] = useState(quiz.slug ?? ``);
    const [introText, setIntroText] = useState(quiz.intro_text || ``);
    const [definition, setDefinition] = useState<QuizDefinition>(() => normalizeDefinition(quiz.draft_definition || emptyDefinition()));
    const [revision, setRevision] = useState(quiz.draft_revision);
    const [publishedVersion, setPublishedVersion] = useState(quiz.published_version_number);
    const [tab, setTab] = useState<`questions` | `results` | `settings`>(`questions`);
    const [errors, setErrors] = useState<string[]>([]);
    const [message, setMessage] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [uploadCount, setUploadCount] = useState(0);
    const contactFields = { ...defaultContactFields, ...definition.contact_fields };

    const questionLabel: QuestionLabel = useMemo(
        () => (id: string) => {
            const question = definition.questions.find((item) => item.id === id);

            return question ? `${question.text.slice(0, 40) || `(без текста)`} [${id.slice(0, 8)}]` : id;
        },
        [definition.questions],
    );

    const showErrors = (error: unknown) => {
        if (error instanceof QuizApiError) {
            if (error.issues.length > 0) {
                setErrors(error.issues.map((issue) => {
                    const context = issue.question_id ? questionLabel(issue.question_id) : issue.result_id ? definition.results.find((item) => item.id === issue.result_id)?.internal_name : ``;
                    return `${context ? `${context}: ` : ``}${issue.message}${issue.option_id ? ` (вариант ${issue.option_id})` : ``}`;
                }));
            } else {
                setErrors([error.message]);
            }
        } else {
            setErrors([(error as Error).message]);
        }
    };

    const handleSave = async () => {
        setLoading(true);
        setErrors([]);
        setMessage(null);

        try {
            const response = await saveDraft(quiz.id, {
                title,
                intro_text: introText || null,
                slug,
                definition,
                expected_revision: revision,
            });

            setRevision(response.draft_revision);
            setMessage(`Черновик сохранён`);
        } catch (error) {
            showErrors(error);
        } finally {
            setLoading(false);
        }
    };

    const handlePublish = async () => {
        setLoading(true);
        setErrors([]);
        setMessage(null);

        try {
            const saved = await saveDraft(quiz.id, { title, intro_text: introText || null, slug, definition, expected_revision: revision });
            setRevision(saved.draft_revision);
            const response = await publishQuiz(quiz.id, saved.draft_revision);
            setRevision(response.draft_revision);
            setPublishedVersion(response.published_version_number);

            setMessage(`Опубликована версия #${response.published_version_number}`);
        } catch (error) {
            showErrors(error);
        } finally {
            setLoading(false);
        }
    };

    const handleArchive = async () => {
        if (!confirm(`Архивировать квиз?`)) {
            return;
        }

        setLoading(true);
        setErrors([]);

        try {
            await archiveQuiz(quiz.id);
            window.location.assign(`/admin/quizzes`);
        } catch (error) {
            showErrors(error);
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!confirm(`Удалить черновик?`)) {
            return;
        }

        setLoading(true);
        setErrors([]);

        try {
            await deleteQuiz(quiz.id);
            window.location.assign(`/admin/quizzes`);
        } catch (error) {
            showErrors(error);
            setLoading(false);
        }
    };

    const questionOptions = definition.questions.map((question) => ({
        value: question.id,
        label: questionLabel(question.id),
    }));

    return (
        <UploadStateContext.Provider value={(delta) => setUploadCount((count) => count + delta)}>
        <fieldset disabled={loading || uploadCount > 0}>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h3>Квиз «{quiz.title}»</h3>
                <a href="/admin/quizzes" className="btn btn-secondary">
                    В список
                </a>
            </div>

            {message && <Alert variant="success">{message}</Alert>}
            {errors.length > 0 && (
                <Alert variant="danger">
                    {errors.map((error) => (
                        <p key={error} className="mb-0">
                            {error}
                        </p>
                    ))}
                </Alert>
            )}

            <ul className="nav nav-tabs mb-3">
                {([`questions`, `results`, `settings`] as const).map((item) => (
                    <li key={item} className="nav-item">
                        <button type="button" className={`nav-link ${tab === item ? `active` : ``}`} onClick={() => setTab(item)}>
                            {item === `questions` ? `Вопросы` : item === `results` ? `Результаты` : `Основные`}
                        </button>
                    </li>
                ))}
            </ul>

            {tab === `questions` && (
                <div>
                    <div className="mb-3">
                        <label className="form-label">Стартовый вопрос</label>
                        <select
                            className="form-select"
                            value={definition.start_question_id || ``}
                            onChange={(event) => setDefinition({ ...definition, start_question_id: event.target.value || null })}
                        >
                            <option value="">Не выбран</option>
                            {questionOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {definition.questions.map((question, index) => (
                        <QuestionEditor
                            key={question.id}
                            question={question}
                            questionLabel={questionLabel}
                            questions={definition.questions.filter((item) => item.id !== question.id)}
                            onDuplicate={() => {
                                const copy: QuizQuestion = {
                                    ...question, id: uid(),
                                    images: question.images.map((image) => ({ ...image })),
                                    default_transition: { ...question.default_transition },
                                    options: question.options.map((option) => ({ ...option, id: uid(), transition: { ...option.transition } })),
                                };
                                const questions = [...definition.questions];
                                questions.splice(index + 1, 0, copy);
                                setDefinition({ ...definition, questions });
                                setMessage(`Вопрос продублирован. Настройте переход к копии, чтобы включить её в прохождение.`);
                            }}
                            onUpdate={(updated) =>
                                setDefinition({
                                    ...definition,
                                    questions: definition.questions.map((item) => (item.id === updated.id ? updated : item)),
                                })
                            }
                            onRemove={() => {
                                const linked = definition.questions.some((item) => item.id !== question.id &&
                                    (item.default_transition.question_id === question.id || item.options.some((option) => option.transition.question_id === question.id)));
                                if (linked) { setErrors([`Сначала измените переходы, ведущие к удаляемому вопросу.`]); return; }
                                setDefinition({
                                    ...definition,
                                    questions: definition.questions.filter((item) => item.id !== question.id),
                                    start_question_id:
                                        definition.start_question_id === question.id ? null : definition.start_question_id,
                                });
                            }}
                            onMove={(direction) => {
                                const newIndex = index + direction;

                                if (newIndex < 0 || newIndex >= definition.questions.length) {
                                    return;
                                }

                                const questions = [...definition.questions];
                                const [moved] = questions.splice(index, 1);
                                questions.splice(newIndex, 0, moved);
                                setDefinition({ ...definition, questions });
                            }}
                        />
                    ))}

                    <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() => setDefinition({ ...definition, questions: [...definition.questions, emptyQuestion()] })}
                    >
                        Добавить вопрос
                    </button>
                </div>
            )}

            {tab === `results` && (
                <div>
                    {definition.results.map((result) => (
                        <ResultEditor
                            key={result.id}
                            result={result}
                            onUpdate={(updated) =>
                                setDefinition({
                                    ...definition,
                                    results: definition.results.map((item) => (item.id === updated.id ? updated : item)),
                                })
                            }
                            onRemove={() =>
                                setDefinition({
                                    ...definition,
                                    results: definition.results.filter((item) => item.id !== result.id),
                                })
                            }
                        />
                    ))}

                    <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() => setDefinition({ ...definition, results: [...definition.results, emptyResult()] })}
                    >
                        Добавить результат
                    </button>
                </div>
            )}

            {tab === `settings` && (
                <div>
                    <div className="mb-2">
                        <label className="form-label">Название</label>
                        <input className="form-control" value={title} onChange={(event) => setTitle(event.target.value)} />
                    </div>

                    {publishedVersion === null && (
                        <div className="mb-2">
                            <label className="form-label">Адрес (slug)</label>
                            <input className="form-control" value={slug} onChange={(event) => setSlug(event.target.value)} />
                        </div>
                    )}

                    <div className="mb-2">
                        <label className="form-label">Вступительный текст</label>
                        <textarea className="form-control" value={introText} onChange={(event) => setIntroText(event.target.value)} />
                    </div>

                    <div className="mb-2">
                        <label className="form-label">Лимит времени (секунд, пусто — без таймера)</label>
                        <input
                            className="form-control"
                            type="number"
                            value={definition.time_limit_seconds ?? ``}
                            onChange={(event) =>
                                setDefinition({
                                    ...definition,
                                    time_limit_seconds: event.target.value === `` ? null : Number(event.target.value),
                                })
                            }
                        />
                    </div>

                    <div className="form-check mb-2">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            checked={definition.allow_back}
                            onChange={(event) => setDefinition({ ...definition, allow_back: event.target.checked })}
                        />
                        <label className="form-check-label">Разрешить возврат назад</label>
                    </div>

                    <div className="mb-2">
                        <label className="form-label">Контактная форма</label>
                        <select
                            className="form-select"
                            value={definition.contact_position}
                            aria-label="Контактная форма"
                            onChange={(event) =>
                                setDefinition({
                                    ...definition,
                                    contact_position: event.target.value as QuizDefinition[`contact_position`],
                                })
                            }
                        >
                            <option value="none">Выключена</option>
                            <option value="first">В начале</option>
                            <option value="last">В конце</option>
                        </select>
                    </div>
                    {definition.contact_position !== `none` && <fieldset className="mb-3">
                        <legend className="h6">Показывать поля контактной формы</legend>
                        {(Object.keys(contactFieldLabels) as Array<keyof typeof contactFieldLabels>).map((field) => (
                            <label key={field} className="form-check">
                                <input type="checkbox" className="form-check-input" checked={contactFields[field]}
                                    onChange={(event) => {
                                        const fields = { ...contactFields, [field]: event.target.checked };
                                        if (!fields.full_name && !fields.phone && !fields.email) {
                                            setErrors([`Оставьте хотя бы одно поле контактов: ФИО, телефон или email.`]);
                                            return;
                                        }
                                        setDefinition({ ...definition, contact_fields: fields });
                                    }} />
                                <span className="form-check-label">{contactFieldLabels[field]}</span>
                            </label>
                        ))}
                        <small>Показанные поля обязательны. Выберите хотя бы одно поле: ФИО, телефон или email.</small>
                    </fieldset>}

                    <hr />
                    <h5>Кнопка запуска в HTML-блоке</h5>
                    <p>Заменять нужно только подпись. Класс и data-quiz сохраняйте; data-quiz-mode: popup — окно, page — отдельная страница. После публикации ссылка станет доступна.</p>
                    <textarea className="form-control mb-2" readOnly aria-label="HTML запуска квиза" value={`<a class="btn js-quiz-launch" href="/quizzes/${slug}" data-quiz="${slug}" data-quiz-mode="popup">Пройти квиз</a>`} />
                    <a href={`/admin/quiz-attempts?quiz_id=${quiz.id}`}>Прохождения и экспорт Excel</a>
                    <hr />
                    <h5>Благодарность</h5>
                    <ImagesEditor images={definition.thank_you.image ? [definition.thank_you.image] : []} limit={1}
                        onChange={(images) => setDefinition({ ...definition, thank_you: { ...definition.thank_you, image: images[0] || null } })} />
                    <div className="mb-2">
                        <label className="form-label">Текст благодарности</label>
                        <textarea
                            className="form-control"
                            value={definition.thank_you.text || ``}
                            onChange={(event) =>
                                setDefinition({
                                    ...definition,
                                    thank_you: { ...definition.thank_you, text: event.target.value || null },
                                })
                            }
                        />
                    </div>
                    <div className="mb-2">
                        <label className="form-label">Подпись кнопки</label>
                        <input
                            className="form-control"
                            value={definition.thank_you.button?.label || ``}
                            onChange={(event) =>
                                setDefinition({
                                    ...definition,
                                    thank_you: {
                                        ...definition.thank_you,
                                        button: event.target.value
                                            ? { label: event.target.value, url: definition.thank_you.button?.url || `` }
                                            : null,
                                    },
                                })
                            }
                        />
                    </div>
                    <div className="mb-2">
                        <label className="form-label">URL кнопки</label>
                        <input
                            className="form-control"
                            value={definition.thank_you.button?.url || ``}
                            onChange={(event) =>
                                setDefinition({
                                    ...definition,
                                    thank_you: {
                                        ...definition.thank_you,
                                        button: definition.thank_you.button
                                            ? { label: definition.thank_you.button.label, url: event.target.value }
                                            : { label: ``, url: event.target.value },
                                    },
                                })
                            }
                        />
                    </div>
                </div>
            )}

            <hr />
            <div className="d-flex gap-2">
                <button type="button" className="btn btn-primary" disabled={loading} onClick={handleSave}>
                    Сохранить черновик
                </button>
                <button type="button" className="btn btn-success" disabled={loading} onClick={handlePublish}>
                    Опубликовать
                </button>
                {quiz.status !== `archived` && (
                    <button type="button" className="btn btn-warning" disabled={loading} onClick={handleArchive}>
                        Архивировать
                    </button>
                )}
                {publishedVersion === null && quiz.status === `draft` && (
                    <button type="button" className="btn btn-danger" disabled={loading} onClick={handleDelete}>
                        Удалить черновик
                    </button>
                )}
            </div>
        </fieldset>
        </UploadStateContext.Provider>
    );
};

export default QuizEditor;
